import { useCallback, useEffect, useState } from "react";
import { Bot, CheckCircle2, RefreshCw } from "lucide-react";
import {
  confirmAssistedExtraction,
  listAssistedExtractions,
  startAssistedExtraction,
} from "@/lib/api";
import type { AssistedExtraction } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export type ExtractionSuggestion = {
  title?: string;
  description?: string;
  neededBy?: string;
  locationHint?: string;
  lines: Array<{
    name: string;
    specification: string;
    quantity: number;
    unit: string;
    estimatedUnitPrice: number;
  }>;
};

function value(item: unknown) {
  if (!item || typeof item !== "object") return undefined;
  return (item as Record<string, unknown>).value;
}

function suggestion(payload: Record<string, unknown>): ExtractionSuggestion {
  const rawLines = Array.isArray(payload.lines) ? payload.lines : [];
  return {
    title: String(value(payload.title) || ""),
    description: String(value(payload.description) || ""),
    neededBy: String(value(payload.needed_by) || ""),
    locationHint: String(value(payload.location_hint) || ""),
    lines: rawLines.map((raw) => {
      const line = raw as Record<string, unknown>;
      return {
        name: String(value(line.name) || ""),
        specification: String(value(line.specification) || ""),
        quantity: Number(value(line.quantity) || 1),
        unit: String(value(line.unit) || "Each"),
        estimatedUnitPrice: Number(value(line.estimated_unit_price) || 0),
      };
    }).filter((line) => line.name || line.specification),
  };
}

export function AssistedExtractionCard({
  organisationId,
  sourceId,
  onApply,
}: {
  organisationId: string;
  sourceId: string;
  onApply: (value: ExtractionSuggestion) => void;
}) {
  const [rows, setRows] = useState<AssistedExtraction[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const latest = rows[0];
  const refresh = useCallback(async () => {
    setRows(await listAssistedExtractions(organisationId, "request_inbox_attachment", sourceId));
  }, [organisationId, sourceId]);
  useEffect(() => { refresh().catch((failure: Error) => setError(failure.message)); }, [refresh]);
  async function extract() {
    setBusy(true); setError("");
    try { await startAssistedExtraction(organisationId, "request_inbox_attachment", sourceId); await refresh(); }
    catch (failure) { setError((failure as Error).message); }
    finally { setBusy(false); }
  }
  async function apply() {
    if (!latest?.result_payload) return;
    setBusy(true); setError("");
    try {
      if (latest.status === "needs_review") {
        await confirmAssistedExtraction(organisationId, latest.id, latest.result_payload);
      }
      onApply(suggestion(latest.result_payload));
      await refresh();
    } catch (failure) { setError((failure as Error).message); }
    finally { setBusy(false); }
  }
  return (
    <div className="mt-2 rounded-md border bg-[var(--surface-muted)] p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2 font-medium"><Bot className="size-4" />Assisted extraction</span>
        {latest && <Badge value={latest.status} />}
      </div>
      {latest?.assisted_extraction_fields?.length ? (
        <div className="mt-2 grid gap-1">
          {latest.assisted_extraction_fields.slice(0, 8).map((field) => (
            <div className="flex justify-between gap-3 text-xs" key={field.id}>
              <span>{field.field_path.replaceAll("/", " › ")}</span>
              <span>{Math.round(Number(field.confidence) * 100)}% · {field.evidence_excerpt || "No excerpt"}</span>
            </div>
          ))}
        </div>
      ) : null}
      {latest?.failure_message && <p className="mt-2 text-[var(--danger)]">{latest.failure_message}</p>}
      {error && <p className="mt-2 text-[var(--danger)]">{error}</p>}
      <div className="mt-3 flex gap-2">
        {(!latest || ["failed","cancelled"].includes(latest.status)) && (
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={extract}>
            <RefreshCw className="size-3.5" />{busy ? "Extracting…" : latest ? "Retry extraction" : "Extract fields"}
          </Button>
        )}
        {latest?.result_payload && ["needs_review","confirmed"].includes(latest.status) && (
          <Button type="button" size="sm" disabled={busy} onClick={apply}>
            <CheckCircle2 className="size-3.5" />Review and apply suggestions
          </Button>
        )}
      </div>
      <p className="mt-2 text-xs text-[var(--text-secondary)]">Suggestions are not procurement facts until you confirm them. You can edit every populated field before creating the draft.</p>
    </div>
  );
}
